import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building, Plus, Trash2, Edit2, TrendingDown, Package } from "lucide-react";

export default function FixedAssets() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", category: "معدات", purchase_date: new Date().toISOString().slice(0, 10),
    purchase_cost: 0, salvage_value: 0, useful_life_years: 5, method: "straight_line",
    depreciation_rate: 20, location: "", serial_number: "", notes: "",
  });

  const load = async () => {
    try {
      const [a, s] = await Promise.all([api.get("/fixed-assets"), api.get("/fixed-assets/report/summary")]);
      setItems(a.data); setSummary(s.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", category: "معدات", purchase_date: new Date().toISOString().slice(0, 10), purchase_cost: 0, salvage_value: 0, useful_life_years: 5, method: "straight_line", depreciation_rate: 20, location: "", serial_number: "", notes: "" });
    setDlg(true);
  };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setDlg(true); };

  const save = async () => {
    if (!form.name.trim() || !form.purchase_cost) return toast.error("املأ الاسم والتكلفة");
    try {
      if (editing) await api.put(`/fixed-assets/${editing.id}`, form);
      else await api.post("/fixed-assets", form);
      toast.success(editing ? "تم التحديث" : "تم إضافة الأصل");
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (a) => {
    if (!window.confirm(`حذف "${a.name}"؟`)) return;
    try { await api.delete(`/fixed-assets/${a.id}`); toast.success("تم"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const dispose = async (a) => {
    const v = window.prompt(`قيمة التخلص/البيع (تكلفة الشراء ${a.purchase_cost}):`, "0");
    if (v === null) return;
    try { await api.post(`/fixed-assets/${a.id}/dispose`, { disposal_value: +v || 0 }); toast.success("تم التخلص"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="fixed-assets-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Building className="w-6 h-6 text-primary" /> الأصول الثابتة</h1>
          <p className="text-sm text-muted-foreground mt-1">تتبع الأصول (معدات، أثاث، سيارات) مع إهلاك تلقائي شهري</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-asset-btn"><Plus className="w-4 h-4" /> أصل جديد</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5"><div className="text-xs text-muted-foreground">إجمالي التكلفة</div><div className="text-2xl font-bold font-mono">{(summary.total_cost || 0).toLocaleString("ar-EG")}</div></div>
          <div className="p-4 rounded-lg border-2 border-amber-200 bg-amber-50"><div className="text-xs text-muted-foreground">إجمالي الإهلاك</div><div className="text-2xl font-bold font-mono text-amber-700">{(summary.total_accumulated_depreciation || 0).toLocaleString("ar-EG")}</div></div>
          <div className="p-4 rounded-lg border-2 border-emerald-200 bg-emerald-50"><div className="text-xs text-muted-foreground">القيمة الدفترية</div><div className="text-2xl font-bold font-mono text-emerald-700">{(summary.total_book_value || 0).toLocaleString("ar-EG")}</div></div>
          <div className="p-4 rounded-lg border bg-card"><div className="text-xs text-muted-foreground">عدد الأصول</div><div className="text-2xl font-bold font-mono">{summary.asset_count || 0}</div></div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground"><Package className="w-16 h-16 mx-auto opacity-30 mb-3" /><div>لا يوجد أصول</div></div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b"><tr><th className="text-start p-3">الأصل</th><th className="text-start p-3">التصنيف</th><th className="text-start p-3">تاريخ الشراء</th><th className="text-start p-3">التكلفة</th><th className="text-start p-3">الإهلاك</th><th className="text-start p-3">القيمة الدفترية</th><th className="text-center p-3">إجراء</th></tr></thead>
            <tbody>{items.map(a => (
              <tr key={a.id} className={`border-b hover:bg-muted/30 ${a.disposed ? "opacity-40" : ""}`} data-testid={`asset-${a.id}`}>
                <td className="p-3"><div className="font-medium">{a.name}</div>{a.serial_number && <div className="text-[10px] text-muted-foreground font-mono">S/N: {a.serial_number}</div>}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-100 text-xs">{a.category}</span></td>
                <td className="p-3 font-mono text-xs">{a.purchase_date}</td>
                <td className="p-3 font-mono">{(a.purchase_cost || 0).toLocaleString("ar-EG")}</td>
                <td className="p-3 font-mono text-amber-700">({(a.accumulated_depreciation || 0).toLocaleString("ar-EG")})</td>
                <td className="p-3 font-mono text-emerald-700 font-bold">{(a.book_value || 0).toLocaleString("ar-EG")}</td>
                <td className="p-3 text-center">
                  {!a.disposed && (
                    <>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dispose(a)} title="تخلّص/بيع"><TrendingDown className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(a)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl" data-testid="asset-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل أصل" : "أصل جديد"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>اسم الأصل</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asset-name" /></div>
            <div><Label>التصنيف</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="معدات">معدات</SelectItem><SelectItem value="أثاث">أثاث</SelectItem><SelectItem value="سيارات">سيارات</SelectItem><SelectItem value="مباني">مباني</SelectItem><SelectItem value="حواسيب">حواسيب</SelectItem><SelectItem value="أخرى">أخرى</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>تاريخ الشراء</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></div>
            <div><Label>تكلفة الشراء</Label><Input type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: +e.target.value || 0 })} data-testid="asset-cost" /></div>
            <div><Label>قيمة الخردة</Label><Input type="number" value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: +e.target.value || 0 })} /></div>
            <div><Label>العمر الإنتاجي (سنوات)</Label><Input type="number" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: +e.target.value || 5 })} /></div>
            <div><Label>طريقة الإهلاك</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="straight_line">قسط ثابت</SelectItem><SelectItem value="declining">قسط متناقص</SelectItem></SelectContent>
              </Select>
            </div>
            {form.method === "declining" && <div><Label>معدل الإهلاك السنوي %</Label><Input type="number" value={form.depreciation_rate} onChange={(e) => setForm({ ...form, depreciation_rate: +e.target.value || 0 })} /></div>}
            <div><Label>الموقع</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div><Label>الرقم التسلسلي</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
            <div className="col-span-2"><Label>ملاحظات</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={save} data-testid="save-asset-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
